import { useState, useEffect } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Stack,
  Button,
  SvgIcon,
} from "@mui/material";
import DomainDetailsStyles from "screens/Settings/Domains/DomainDetails/DomainDetails.styles";
import {
  copyTextHandler,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import { getDomainDetails } from "screens/Settings/Domains/Domains.service";
import { domainDetailsTableHeader } from "screens/Settings/Domains/DomainsHelpers";
import copyIcon from "assets/icons1/copyIcon.svg";
import redInfoIcon from "assets/icons1/redInfoIcon.svg";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomTable from "global/components/CustomTable/CustomTable";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import need_help from "../../../../assets/icons1/need_help.svg";
import strings from "global/constants/StringConstants";
import CelebrationIcon from "@mui/icons-material/Celebration";

interface CustomProps {
  domainStatus?: string;
  domainToBeDisplayed: string;
  isLoading: boolean;
  setIsLoading: Function;
  domainViewButtonHandler: Function;
  isStartVerification: boolean;
  setIsStartVerification: Function;
  isDomainDetailsPage: boolean;
  verifyStatusButtonHandler: any;
  setIsOpenStartVerification: Function;
}

const DomainDetails = (props: CustomProps) => {
  const classes = DomainDetailsStyles;
  const [isLoading, setIsLoading] = useState(false);
  const [domainDetails, setDomainDetails] = useState<any>({});

  useEffect(() => {
    fetchDomainStatus();
  }, []);

  const fetchDomainStatus = async () => {
    try {
      props.setIsLoading(true);
      const response = await getDomainDetails(props.domainToBeDisplayed);
      props.setIsStartVerification(
        !!(
          response?.data?.custom_envelope?.valid && response?.data?.dkim?.valid
        )
      );
      const formattedRows = getFormattedDomainDetails(response);
      setDomainDetails(formattedRows);
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      props.setIsLoading(false);
    }
  };

  const getFormattedDomainDetails = (rawDomainDetails: any) => {
    const formattedRowArray: any[] = [
      {
        ...getTableRow(
          rawDomainDetails.data.custom_envelope.host,
          rawDomainDetails.data.custom_envelope.type,
          rawDomainDetails.data.custom_envelope.required_value,
          rawDomainDetails.data.custom_envelope.valid
        ),
      },
      {
        ...getTableRow(
          rawDomainDetails.data.dkim.host,
          rawDomainDetails.data.dkim.type,
          rawDomainDetails.data.dkim.required_value,
          rawDomainDetails.data.dkim.valid
        ),
      },
    ];
    return formattedRowArray;
  };

  const getStatus = (status: number) => {
    if (status === 0) {
      return "Not Updated";
    }
    if (status === 1) {
      return "Updated";
    }
  };

  const getColors = {
    0: "#FF9436",
    1: "#47AA12",
  } as { [key: number]: string };

  const getDomainStatusColors = {
    CNAMEPending: "#FF9436",
    VerificationPending: "#FF9436",
    Active: "#47AA12",
  } as { [key: string]: string };

  const getTableRow = (
    hostName: string,
    type: String,
    requiredValue: string,
    status: number
  ) => {
    return {
      hostName: hostName,
      type: type,
      requiredValue: (
        <Box>
          <TextField
            disabled
            sx={classes.textField}
            variant="outlined"
            value={requiredValue}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img
                    src={copyIcon}
                    onClick={() => {
                      copyTextHandler(requiredValue);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={classes.requiredValueDescriptionBox}>
            <img src={redInfoIcon} style={classes.redInfoIconStyles} />
            <Typography sx={classes.requiredValueInfo} variant="subtitle2">
              Copy the record and place it in your DNS settings
            </Typography>
          </Box>
        </Box>
      ),
      status: (
        <Typography
          sx={{
            color: getColors[status],
            display: "flex",
            alignItems: "center",
          }}
          variant="subtitle2"
        >
          <Box
            sx={{
              backgroundColor: getColors[status],
            }}
          />
          {getStatus(status)}
        </Typography>
      ),
    };
  };

  const getSubHeading = () => {
    return (
      <Stack spacing={1} direction="row">
        <Stack direction="column" spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4" color="text.primary">
                Authenticate {props.domainToBeDisplayed}
              </Typography>
              <Typography
                sx={{
                  color: getDomainStatusColors[props?.domainStatus!],
                  display: "flex",
                  alignItems: "center",
                }}
                variant="subtitle2"
              >
                <Box
                  sx={{
                    borderRadius: "50%",
                    height: 6,
                    width: 6,
                    mx: 0.5,
                    backgroundColor:
                      getDomainStatusColors[props?.domainStatus!],
                  }}
                />
                {strings.EmailDomainStatusDisplayValue[props?.domainStatus!]}
              </Typography>
            </Stack>
            {props.isDomainDetailsPage && (
              <Stack
                direction={{ lg: "row", md: "row", sm: "row", xs: "column" }}
                spacing={2}
              >
                <Button
                  id="domains_status_verify_button"
                  variant="outlined"
                  size="small"
                  onClick={props.verifyStatusButtonHandler}
                  disabled={isLoading}
                >
                  Refresh status
                </Button>
                <Button
                  id="domains_start_verification_button"
                  variant="contained"
                  size="small"
                  onClick={() => props.setIsOpenStartVerification(true)}
                  disabled={
                    !(
                      props.isStartVerification &&
                      props.domainStatus ===
                        strings.EmailDomainStatus.CNAMEPending
                    )
                  }
                >
                  Complete authentication
                </Button>
              </Stack>
            )}
          </Stack>
          <Typography variant="body2" color="text.primary">
            Please paste the provided values into your email provider's account
            and click "Refresh status". Once "Updated" appears under status
            column, click "Complete Authentication" and answer the required
            questions. Authentication may take up to 24 hours after the
            questions are answered.
          </Typography>
          {props.domainStatus ===
            strings.EmailDomainStatus.VerificationPending && (
            <Stack
              p={1.5}
              direction="row"
              sx={{ backgroundColor: "rgb(117, 81, 208)", borderRadius: "8px" }}
              alignItems="center"
            >
              <SvgIcon sx={{ mr: 1 }}>
                <CelebrationIcon htmlColor="#ffc64c" />
              </SvgIcon>
              <Typography variant="body2" color="#FFF">
                Thank you for submitting your answers. Our domain approval team
                is currently reviewing your details, and this process may take
                up to 24 hours.
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  const getDomainDetailsTable = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Box>
          <CustomTable
            headers={domainDetailsTableHeader}
            rows={domainDetails}
            isLoading={props.isLoading}
            showPagination={false}
          />
        </Box>
      </CenteredCardLayout>
    );
  };

  const getDomainDetailsPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getSubHeading()}
        {getDomainDetailsTable()}
        <Typography color={"#FF9436"} variant="body2">
          <span style={{ marginRight: 4 }}>
            <img src={need_help} />
          </span>
          Need help? Check our help article or contact our support. We’re here
          to help. 
        </Typography>
        <CustomLoader isLoading={isLoading} />
      </Stack>
    );
  };

  return getDomainDetailsPage();
};

export default DomainDetails;
